import type { ReactElement } from 'react';
import React from 'react';
import { ApolloProvider } from 'react-apollo';

import type {
	ExtensionParams,
	ExtensionHandler,
} from '@atlaskit/editor-common/extensions/extension-handler';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { SessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { SessionDataContext } from '@confluence/session-data/entry-points/SessionDataContext.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';

// type ExtensionHandler<T> = (ext: ExtensionParams<T>, doc: Object)
export const createMacroExtensionHandler =
	(extensionHandler: ExtensionHandler<any>) =>
	(extension: ExtensionParams<any>, ...args: any[]): ReactElement => (
		<ErrorBoundary attribution={Attribution.PAGE_EXTENSIONS}>
			<ApolloProvider client={getApolloClient()}>
				<SessionData>
					{(sessionData) => (
						<SessionDataContext.Provider value={sessionData}>
							{extensionHandler(extension, args)}
						</SessionDataContext.Provider>
					)}
				</SessionData>
			</ApolloProvider>
		</ErrorBoundary>
	);

/**
 * Variant of `createMacroExtensionHandler` for handlers that need to decline a node.
 *
 * `createMacroExtensionHandler` always returns an element, so a handler that produced nothing is
 * indistinguishable from one that rendered — and `ExtensionRenderer` only falls through to the
 * extension provider (the manifest) when the handler result is falsy. This variant asks the handler
 * up front, once, and returns `null` when it declines so the manifest can take the node instead.
 *
 * The handler is invoked outside the `SessionData`/Apollo context that wraps the rendered output, so
 * it must be a pure element factory; anything needing that context has to be inside the element it
 * returns, which is how the SSR macro handlers are already written.
 */
export const createDeclinableMacroExtensionHandler =
	(extensionHandler: (extension: ExtensionParams<any>, args: any[]) => ReactElement | null) =>
	(extension: ExtensionParams<any>, ...args: any[]): ReactElement | null => {
		const content = extensionHandler(extension, args);

		if (content === null) {
			return null;
		}

		return (
			<ErrorBoundary attribution={Attribution.PAGE_EXTENSIONS}>
				<ApolloProvider client={getApolloClient()}>
					<SessionData>
						{(sessionData) => (
							<SessionDataContext.Provider value={sessionData}>
								{content}
							</SessionDataContext.Provider>
						)}
					</SessionData>
				</ApolloProvider>
			</ErrorBoundary>
		);
	};
